import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, ArrowRight, PartyPopper, Wrench, RefreshCw, Lock } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { BrandButton } from "./TenantShell";
import { tapi, apiErr, fmtKes } from "../../lib/tenant";
import { PUBLIC_APP_URL } from "../../lib/api";
import { detectHostTenant } from "../../lib/tenant";

const CheckRow = ({ c }) => (
  <li className="flex items-start gap-3 rounded-xl border border-neutral-200 bg-white p-3" data-testid={`outcome-check-${c.key}`} data-passed={c.passed}>
    {c.passed ? <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-600" /> : <XCircle size={18} className="mt-0.5 shrink-0 text-red-500" />}
    <div className="min-w-0"><p className="text-sm font-semibold">{c.label}</p><p className="text-xs text-neutral-500">{c.actual}</p>{c.why && <p className="mt-1 text-xs text-red-700">{c.why}</p>}</div>
  </li>
);

const Facility = ({ f, name }) => (
  <div className="rounded-2xl p-5 text-white" style={{ background: "var(--tenant)" }} data-testid="outcome-facility">
    <p className="text-[11px] font-bold uppercase tracking-[0.2em] opacity-80">Indicative facility</p>
    <p className="mt-1 text-3xl font-extrabold" data-testid="outcome-facility-amount">{fmtKes(f.amount_kes)}</p>
    <div className="mt-3 grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
      <div><p className="opacity-70">Tenor</p><p className="font-bold" data-testid="outcome-facility-tenor">{f.tenor_months} months</p></div>
      <div><p className="opacity-70">Est. monthly repayment</p><p className="font-bold">{fmtKes(f.monthly_repayment_kes)}</p></div>
      <div><p className="opacity-70">Indicative rate</p><p className="font-bold">{f.interest_rate_pct}% p.a.</p></div>
    </div>
    <p className="mt-3 text-[11px] opacity-80">Sized so repayments keep your DSCR at or above {f.dscr_used}x, based on {f.noi_source}. {f.requested_kes ? `You asked for ${fmtKes(f.requested_kes)}.` : ""} Indicative only — final terms are set by {name}.</p>
  </div>
);

export const StepOutcome = ({ me, onRefreshMe, onRerun }) => {
  const { sub, config } = useTenant();
  const [outcome, setOutcome] = useState(me.outcome || null);
  const [loading, setLoading] = useState(!me.outcome);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async (refresh = false) => {
    setLoading(true); setError("");
    try { const { data } = await tapi.get(`/t/${sub}/outcome${refresh ? "?refresh=1" : ""}`); setOutcome(data); }
    catch (e) { setError(apiErr(e, "Couldn't compute your outcome")); }
    setLoading(false);
  };
  useEffect(() => { if (!me.outcome) load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [linkSentTo, setLinkSentTo] = useState("");
  const getReport = async () => {
    setBusy(true);
    try {
      const { data } = await tapi.post(`/t/${sub}/handoff`);
      if (data.mode === "auto_login") {
        const base = detectHostTenant() ? PUBLIC_APP_URL : window.location.origin;
        window.location.href = `${base}/handoff?token=${encodeURIComponent(data.token)}`;
        return;
      }
      setLinkSentTo(data.email || me.user.email);
      toast.success("Check your inbox for a secure sign-in link");
    } catch (e) { toast.error(apiErr(e, "Couldn't set up your Clinq account")); }
    setBusy(false);
  };

  const reruns = me.reruns || { total_left: 0, free_left: 0, paid_left: 0, pack_size: 3, pack_price_kes: 1500 };
  const unlockReruns = async () => {
    setBusy(true);
    try { const { data } = await tapi.post(`/t/${sub}/reruns/checkout`); window.location.assign(data.checkout_url); }
    catch (e) { toast.error(apiErr(e, "Couldn't start payment")); setBusy(false); }
  };
  const rerunLabel = reruns.total_left > 0
    ? `Re-check with new documents · ${reruns.total_left} ${reruns.free_left > 0 ? "free " : ""}re-run${reruns.total_left === 1 ? "" : "s"} left`
    : `Unlock ${reruns.pack_size} more re-runs — KES ${Number(reruns.pack_price_kes).toLocaleString()}`;

  const connect = async () => {
    setBusy(true);
    try { await tapi.post(`/t/${sub}/connect`); toast.success(`${config.name} has been notified — they will be in touch.`); await onRefreshMe(); }
    catch (e) { toast.error(apiErr(e, "Couldn't send your request")); }
    setBusy(false);
  };

  if (loading) return <div className="flex flex-col items-center gap-3 py-16" data-testid="outcome-loading"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--tenant)" }} /><p className="text-sm text-neutral-500">Checking your results against {config.name}'s criteria…</p></div>;
  if (error) return <div className="space-y-3 rounded-2xl border border-neutral-200 bg-white p-6 text-center" data-testid="outcome-error"><p className="text-sm text-red-700">{error}</p><BrandButton onClick={() => load(true)}>Try again</BrandButton></div>;

  const q = outcome.qualified;
  return (
    <div className="space-y-5" data-testid="tenant-step-outcome" data-outcome={q ? "qualified" : "not_yet"}>
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <span className={"flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl " + (q ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")}>{q ? <PartyPopper size={22} /> : <Wrench size={22} />}</span>
          <div>
            <h2 className="text-xl font-extrabold leading-snug" data-testid="outcome-headline">
              {q ? `Good news — based on the information provided, you may qualify for a ${config.name} business loan.` : "You are not quite ready for a loan yet — here is what to work on."}
            </h2>
            <p className="mt-1 text-sm text-neutral-500">Capital Readiness Score: <b className="text-neutral-800" data-testid="outcome-score">{outcome.score}</b> · {outcome.checks.filter((c) => c.passed).length} of {outcome.checks.length} checks passed</p>
          </div>
        </div>
        {q && outcome.facility && <div className="mt-5"><Facility f={outcome.facility} name={config.name} /></div>}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          {q ? (
            me.lead?.connected_at
              ? <span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-700" data-testid="outcome-connected"><CheckCircle2 size={16} /> Request sent — {config.name} will contact you</span>
              : <BrandButton onClick={connect} disabled={busy} data-testid="outcome-connect-btn">{busy && <Loader2 size={14} className="animate-spin" />} Connect me with {config.name} <ArrowRight size={16} /></BrandButton>
          ) : (
            <BrandButton onClick={getReport} disabled={busy} data-testid="outcome-report-btn">{busy && <Loader2 size={14} className="animate-spin" />} Get my Capital Readiness Report <ArrowRight size={16} /></BrandButton>
          )}
          <button type="button" onClick={reruns.total_left > 0 ? onRerun : unlockReruns} disabled={busy}
            className={"inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 " + (reruns.total_left > 0 ? "text-neutral-600 hover:bg-neutral-100" : "border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100")}
            data-testid="outcome-recheck" data-reruns-left={reruns.total_left}>
            {reruns.total_left > 0 ? <RefreshCw size={12} /> : <Lock size={12} />} {rerunLabel}
          </button>
        </div>
        <p className="mt-2 text-[11px] text-neutral-400" data-testid="outcome-rerun-hint">
          Uploaded new or updated documents? Re-check re-scores your business from scratch. {reruns.free_total ?? 3} re-runs are free; after that, {reruns.pack_size} more cost KES {Number(reruns.pack_price_kes).toLocaleString()}.
        </p>
        {!q && linkSentTo && <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid="outcome-handoff-email-sent"><CheckCircle2 size={14} /> You already have a Clinq account — we've emailed a secure sign-in link to {linkSentTo}. Open it to get your report.</p>}
      </div>
      <div>
        <h3 className="mb-2 text-sm font-bold text-neutral-700">{q ? "Checks we ran" : "What you did not meet — and why"}</h3>
        <ul className="space-y-2" data-testid="outcome-checks">{(q ? outcome.checks : [...outcome.failed, ...outcome.checks.filter((c) => c.passed)]).map((c) => <CheckRow key={c.key} c={c} />)}</ul>
        {outcome.custom_disqualifiers?.length > 0 && <p className="mt-3 text-[11px] text-neutral-400">{config.name} will also review: {outcome.custom_disqualifiers.join(" · ")}.</p>}
      </div>
    </div>
  );
};
